import { useEffect } from 'react';

/**
 * Comprehensive SEO helper — sets:
 *  - <title>
 *  - <meta name="description">, keywords, robots
 *  - <link rel="canonical">
 *  - Open Graph (title, description, url, type, site_name, locale, image)
 *  - Twitter Card (card, title, description, image)
 *  - JSON-LD structured data (accepts a single object OR an array of objects)
 *
 * All tags are updated in-place (or created if missing) — no duplicates.
 * Multi-JSON-LD tags are indexed by data-seo-id so a page can register several
 * scripts (e.g. LocalBusiness + BreadcrumbList + FAQPage).
 */
const BRAND_NAME = 'Groupe Ambulances Beaulon';
const DEFAULT_OG_IMAGE = 'https://ambulances-beaulon.com/og-image.jpg';

export function useSEO({
  title,
  description,
  keywords,
  jsonLd,
  canonical,
  ogImage,
  ogType = 'website',
  robots = 'index, follow, max-image-preview:large, max-snippet:-1',
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', value);
    };

    // Standard meta
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'robots', robots);
    setMeta('name', 'author', BRAND_NAME);

    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:site_name', BRAND_NAME);
    setMeta('property', 'og:locale', 'fr_FR');
    setMeta('property', 'og:image', ogImage || DEFAULT_OG_IMAGE);
    setMeta('property', 'og:image:alt', title);

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage || DEFAULT_OG_IMAGE);

    // Canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Remove any previously-injected JSON-LD from prior page
    document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());

    // Inject JSON-LD — accepts either a single object or an array of objects
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((entry, i) => {
        if (!entry) return;
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', String(i));
        script.text = JSON.stringify(entry);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogImage, ogType, robots, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
}

// --------- Helpers to build common JSON-LD structures ---------

export const SITE_URL = 'https://ambulances-beaulon.com';

export function buildBreadcrumb(items) {
  // items: [{ name, url? }]
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}

export function buildFAQPage(faqs) {
  // faqs: [{ q, a }]
  if (!faqs || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function buildOrganization() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description: 'Groupe Ambulances Beaulon : transport sanitaire conventionné (ambulances, VSL, taxis) dans l\'Allier (03). 4 implantations locales : Garnat-sur-Engièvre, Avermes/Saint-Ennemond, Diou et Tronget/Le Montet.',
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Allier' },
      { '@type': 'AdministrativeArea', name: 'Auvergne-Rhône-Alpes' },
    ],
    contactPoint: [
      { '@type': 'ContactPoint', telephone: '+33 4 70 42 72 63', contactType: 'customer service', areaServed: 'FR', availableLanguage: ['French'], name: 'Ambulances Beaulon – Garnat-sur-Engièvre' },
      { '@type': 'ContactPoint', telephone: '+33 6 56 66 05 71', contactType: 'customer service', areaServed: 'FR', availableLanguage: ['French'], name: 'Ambulance 03 – Avermes / Saint-Ennemond' },
      { '@type': 'ContactPoint', telephone: '+33 4 70 42 90 08', contactType: 'customer service', areaServed: 'FR', availableLanguage: ['French'], name: 'Céline Ambulance – Diou' },
      { '@type': 'ContactPoint', telephone: '+33 4 70 47 10 30', contactType: 'customer service', areaServed: 'FR', availableLanguage: ['French'], name: 'Ambulance Tronget – Le Montet' },
    ],
    subOrganization: [
      { '@type': 'MedicalBusiness', name: 'Ambulances Beaulon – Garnat-sur-Engièvre', url: `${SITE_URL}/ambulance-beaulon-garnat`, telephone: '+33470427263' },
      { '@type': 'MedicalBusiness', name: 'Ambulance 03 – Avermes / Saint-Ennemond', url: `${SITE_URL}/ambulance-03-avermes`, telephone: '+33656660571' },
      { '@type': 'MedicalBusiness', name: 'Céline Ambulance – Diou', url: `${SITE_URL}/celine-ambulance-diou`, telephone: '+33470429008' },
      { '@type': 'MedicalBusiness', name: 'Ambulance Tronget – Le Montet', url: `${SITE_URL}/ambulance-tronget-le-montet`, telephone: '+33470471030' },
    ],
    sameAs: [], // add social profiles here if any
  };
}

export function buildWebSite() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: SITE_URL,
    name: BRAND_NAME,
    inLanguage: 'fr-FR',
  };
}
